/**
 * Estados e blocos de layout da pré-análise eletrônica (E78.F3).
 *
 * Mesmo recorte de `MesaEletronicaStates`: erro, sucesso, carregando e vazio
 * saem da página para que ela seja composição. Sem isso, as ramificações
 * somadas às da tabela estouram o limite de complexidade do lint — que aqui
 * está certo, porque a página passa a ser difícil de ler.
 */
import { TabelaDePecas, type TabelaDePecasProps } from '@/components/ExameEletronico/TabelaDePecas';
import { Blueprint } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoCasoData } from '@/lib/exame_eletronico';
import { confrontoDaPeca } from '@/lib/exame_eletronico_peca';

const t = ptBR.exameEletronico;
const tp = ptBR.exameEletronico.tabelaDePecas;

export function PreAnaliseAvisos({
  erroGlobal,
  sucessoGlobal,
  caso,
}: {
  erroGlobal: string | null;
  sucessoGlobal: string | null;
  caso: ExameEletronicoCasoData | null;
}): JSX.Element {
  return (
    <>
      {erroGlobal !== null ? (
        <p
          className="mt-sc-4 border border-danger bg-danger-50 px-sc-3 py-sc-2 text-body-sm text-danger-700"
          role="alert"
          data-testid="pre-analise-error"
        >
          {erroGlobal}
        </p>
      ) : null}
      {sucessoGlobal !== null ? (
        <p
          className="mt-sc-4 border border-success/30 bg-success-50 px-sc-3 py-sc-2 text-body-sm text-success-700"
          role="status"
          data-testid="pre-analise-sucesso"
        >
          {sucessoGlobal}
        </p>
      ) : null}
      {caso === null && erroGlobal === null ? (
        <p className="mt-sc-6 text-body-md text-fg-muted" data-testid="pre-analise-loading">
          {t.loading}
        </p>
      ) : null}
      {caso !== null && caso.pecas.length === 0 ? (
        <p className="mt-sc-6 text-body-md text-fg-muted" data-testid="pre-analise-needs-contested">
          {t.semCcbs}
        </p>
      ) : null}
    </>
  );
}

/** A seção das peças: quantas foram verificadas, quantas divergiram, e a tabela. */
export function SecaoDePecas(props: TabelaDePecasProps): JSX.Element {
  const { pecas } = props;
  const verificadas = pecas.filter((p) => p.exame?.resultado != null).length;
  const divergentes = pecas.filter((p) => confrontoDaPeca(p) === 'nao_confere').length;
  const resumo = (divergentes > 0 ? tp.resumo : tp.resumoSemDivergencia)
    .replace('{verificadas}', String(verificadas))
    .replace('{total}', String(pecas.length))
    .replace('{divergentes}', String(divergentes));
  return (
    <Blueprint as="section" className="mt-sc-6 space-y-sc-4 p-sc-6" data-testid="pre-analise-lista">
      <header>
        <h2 className="text-display-sm text-fg">{tp.titulo}</h2>
        <p className="mt-1 text-body-sm text-fg-muted" data-testid="pecas-resumo">
          {resumo}
        </p>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{tp.intro}</p>
      </header>
      <TabelaDePecas {...props} />
    </Blueprint>
  );
}
